import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getGerencias,
  getCoordenadores,
  getSubs,
  getSbs,
  validarKm,
  sbKm,
  getAtivosNomes,
  getAtividadesNomes,
} from "../api";
import api from "../../../api/axios";
import "./EditarModal.css";

/* ================= Utils ================= */
const keyify = (s) => String(s || "").trim().toUpperCase();
const cleanDec = (raw) => String(raw ?? "").replace(",", ".").replace(/[^\d.-]/g, "");
const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const pickArray = (resp) =>
  Array.isArray(resp) ? resp :
  Array.isArray(resp?.data) ? resp.data :
  Array.isArray(resp?.data?.data) ? resp.data.data : [];

const unitFromRow = (row) =>
  row?.unidade ?? row?.unidade_medida ?? row?.medida ?? row?.unid ?? "";

const firstNonEmpty = (o, keys, fallback = "") => {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const canonProgramacao = (p = {}) => ({
  atividade: firstNonEmpty(p, ["atividade", "atividade_nome"]),
  unidade_medida: firstNonEmpty(p, ["unidade_medida", "unidade", "medida", "unid"]),
  ativo: firstNonEmpty(p, ["ativo", "ativos", "ativo_nome"]),
  gerencia: firstNonEmpty(p, ["gerencia"]),
  coordenador: firstNonEmpty(p, ["coordenador"]),
  sub: firstNonEmpty(p, ["sub", "subdivisao_id", "sub_id"]),
  kmInicio: firstNonEmpty(p, ["kmInicio", "km_inicio"]),
  kmFinal:  firstNonEmpty(p, ["kmFinal", "km_final"]),
  horario_do_intervalo: firstNonEmpty(p, ["horario_do_intervalo", "horario_intervalo", "hora_intervalo"]),
  tempo_do_intervalo:   firstNonEmpty(p, ["tempo_do_intervalo", "tempo_intervalo"]),
  sbOrigem:       firstNonEmpty(p, ["sbOrigem", "sb_origem", "patio_origem"]),
  sbDeTrabalho:   firstNonEmpty(p, ["sbDeTrabalho", "sb_de_trabalho", "sb_trabalho"]),
  sbEncerramento: firstNonEmpty(p, ["sbEncerramento", "sb_encerramento", "patio_encerramento"]),
  qtd: p?.qtd ?? p?.quantidade ?? "",
  quantidade_maquinista: firstNonEmpty(p, ["quantidade_maquinista", "qtd_maquinista", "maquinistas"]),
  maquinista: firstNonEmpty(p, ["maquinista"]),
  locomotiva1: firstNonEmpty(p, ["locomotiva1", "locomotiva_1"]),
  locomotiva2: firstNonEmpty(p, ["locomotiva2", "locomotiva_2"]),
  tipo_programacao: firstNonEmpty(p, ["tipo_programacao", "tipoProg", "tipo"]),
  prioridade: String(firstNonEmpty(p, ["prioridade"], "")).toUpperCase(),
  tipo_acionamento: firstNonEmpty(p, ["tipo_acionamento", "acionamento_tipo"]),
  observacao: firstNonEmpty(p, ["observacao"]),
  codigo_unico: firstNonEmpty(p, ["codigo_unico"]),
});

/* Normalizador de SB e util de unique+sort */
const normalizeSbItem = (x) => (x && typeof x === "object"
  ? String(x.sb ?? x.patio ?? x.nome ?? x.label ?? "").trim()
  : String(x ?? "").trim());
const uniqSorted = (arr) => Array.from(new Set(arr.filter(Boolean))).sort((a,b)=>a.localeCompare(b,"pt-BR"));

/* Mini diálogo de erro */
function ErrorDialog({ open, message, onClose }) {
  if (!open) return null;
  return (
    <div className="ed-alert__backdrop" role="dialog" aria-modal="true">
      <div className="ed-alert__card">
        <div className="ed-alert__title">Verifique os campos</div>
        <div className="ed-alert__msg">{message}</div>
        <button className="ed-alert__btn" onClick={onClose}>OK</button>
      </div>
    </div>
  );
}

function FieldDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  title = "Selecione",
  disabled = false,
  invalid = false,
  searchable = false,
  searchPlaceholder = "Buscar...",
  triggerRef: externalTriggerRef = null,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) => String(opt ?? "").trim())
        .filter(Boolean),
    [options]
  );

  useEffect(() => {
    if (!externalTriggerRef) return;
    externalTriggerRef.current = triggerRef.current;
  }, [externalTriggerRef, open]);

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchorEl = triggerRef.current || rootRef.current;
      if (!anchorEl) return;

      const rect = anchorEl.getBoundingClientRect();
      const boundary =
        anchorEl.closest(".editar-modal__body") ||
        anchorEl.closest(".editar-modal__container");
      const boundaryRect = boundary?.getBoundingClientRect?.() || null;
      const gap = 8;
      const idealHeight = searchable ? 320 : 260;
      const spaceBelow = boundaryRect ? boundaryRect.bottom - rect.bottom : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      const nextOpenUp = spaceBelow < idealHeight && spaceAbove > spaceBelow;
      const available = Math.max(
        140,
        Math.min(idealHeight, Math.floor((nextOpenUp ? spaceAbove : spaceBelow) - gap))
      );

      setOpenUp(nextOpenUp);
      setMenuStyle(
        nextOpenUp
          ? {
              left: `${Math.round(rect.left)}px`,
              width: `${Math.round(rect.width)}px`,
              bottom: `${Math.round(window.innerHeight - rect.top + gap)}px`,
              maxHeight: `${available}px`,
            }
          : {
              top: `${Math.round(rect.bottom + gap)}px`,
              left: `${Math.round(rect.left)}px`,
              width: `${Math.round(rect.width)}px`,
              maxHeight: `${available}px`,
            }
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, searchable]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    if (!searchable) return undefined;
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open, searchable]);

  const normalizedQuery = useMemo(() => normalizeSearchText(query), [query]);
  const filteredOptions = useMemo(() => {
    if (!searchable || !normalizedQuery) return safeOptions;
    return safeOptions.filter((opt) => normalizeSearchText(opt).includes(normalizedQuery));
  }, [normalizedQuery, safeOptions, searchable]);

  const handleClear = () => {
    onChange("");
    setQuery("");
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`ed-select ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""} ${
        invalid ? "is-invalid" : ""
      }`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className="inp ed-select__trigger"
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`ed-select__value ${value ? "" : "is-placeholder"}`.trim()}>
          {value || placeholder}
        </span>
      </button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className={`ed-select__menu is-floating ${openUp ? "is-up" : ""}`.trim()}
              role="listbox"
              style={menuStyle}
            >
              <div className="ed-select__header">
                <div className="ed-select__headerCopy">
                  <span className="ed-select__title">{title}</span>
                  <span className="ed-select__meta">
                    {value ? "1 selecionado" : "Nenhum selecionado"}
                  </span>
                </div>
                <button
                  type="button"
                  className="ed-select__clearBtn"
                  onClick={handleClear}
                  disabled={!value}
                >
                  Limpar
                </button>
              </div>

              {searchable && (
                <div className="ed-select__searchWrap">
                  <input
                    ref={searchRef}
                    type="text"
                    className="ed-select__search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                  />
                </div>
              )}

              <div className="ed-select__list">
                {filteredOptions.map((opt) => {
                  const selected = opt === value;
                  return (
                    <button
                      key={opt}
                      type="button"
                      className={`ed-select__option ${selected ? "is-selected" : ""}`.trim()}
                      onClick={() => {
                        onChange(opt);
                        setOpen(false);
                      }}
                      aria-selected={selected}
                    >
                      {opt}
                    </button>
                  );
                })}

                {!filteredOptions.length && (
                  <div className="ed-select__empty">Nenhum resultado encontrado.</div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/* ================= Componente ================= */
export default function EditarModal({ programacao = {}, onClose = () => {}, onSaved = () => {} }) {
  const prog = useMemo(() => canonProgramacao(programacao), [programacao]);

  /* Estado base */
  const [gerencias, setGerencias] = useState([]);
  const [coordenadores, setCoordenadores] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sbsAll, setSbsAll] = useState([]);
  const [sbsCoord, setSbsCoord] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [salvando, setSalvando] = useState(false);

  /* Form */
  const [form, setForm] = useState(() => ({
    atividade: prog.atividade || "",
    unidade_medida: prog.unidade_medida || "",
    ativo: prog.ativo || "",
    gerencia: prog.gerencia || "",
    coordenador: prog.coordenador || "",
    sub: prog.sub || "",
    kmInicio: prog.kmInicio || "",
    kmFinal: prog.kmFinal || "",
    horario_do_intervalo: prog.horario_do_intervalo || "",
    tempo_do_intervalo: prog.tempo_do_intervalo || "",
    sbOrigem: prog.sbOrigem || "",
    sbDeTrabalho: prog.sbDeTrabalho || "",
    sbEncerramento: prog.sbEncerramento || "",
    qtd: prog.qtd ?? "",
    quantidade_maquinista: prog.quantidade_maquinista || "",
    maquinista: prog.maquinista || "",
    locomotiva1: prog.locomotiva1 || "",
    locomotiva2: prog.locomotiva2 || "",
    tipo_programacao: prog.tipo_programacao || "",
    prioridade: prog.prioridade || "",
    tipo_acionamento: prog.tipo_acionamento || "",
    kmValido: true,
    sbMsg: "",
  }));

  /* Refs */
  const atividadeRef = useRef(null);
  const ativoRef = useRef(null);
  const qtdRef = useRef(null);

  /* Diálogo de erro */
  const [err, setErr] = useState({ open: false, msg: "" });
  const showErr = (msg, focusRef) => {
    setErr({ open: true, msg });
    setTimeout(() => focusRef?.current?.focus?.(), 0);
  };

  /* Índices */
  const atividadeIndex = useMemo(() => {
    const map = new Map();
    (atividades || []).forEach((row) => {
      const nome = String(row?.atividade || row?.nome || row?.label || row || "").trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { atividade: nome, unidade: unitFromRow(row) });
    });
    return map;
  }, [atividades]);

  const ativoIndex = useMemo(() => {
    const map = new Map();
    (ativos || []).forEach((row) => {
      const nome = String(row?.ativos || row?.ativo || row?.nome || row?.label || row || "").trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { ativos: nome });
    });
    return map;
  }, [ativos]);

  const atividadeValida = !!(form.atividade && atividadeIndex.has(keyify(form.atividade)));
  const ativoValido = !!(form.ativo && ativoIndex.has(keyify(form.ativo)));

  /* Opções */
  const atividadesOptions = useMemo(() => {
    const set = new Set((atividades || []).map((r) => String(r?.atividade || r || "")).filter(Boolean));
    if (form.atividade) set.add(form.atividade);
    return Array.from(set).sort((a,b) => a.localeCompare(b,"pt-BR"));
  }, [atividades, form.atividade]);

  const ativosOptions = useMemo(() => {
    const set = new Set((ativos || []).map((r) => String(r?.ativos || r?.ativo || r || "")).filter(Boolean));
    if (form.ativo) set.add(form.ativo);
    return Array.from(set).sort((a,b) => a.localeCompare(b,"pt-BR"));
  }, [ativos, form.ativo]);

  const subsIds = useMemo(
    () => subs.map((s) => s.subdivisao_id ?? s.sub ?? s.id).filter(Boolean),
    [subs]
  );

  /* Loads */
  useEffect(() => {
    (async () => {
      try { setGerencias((await getGerencias()) || []); } catch {}

      try {
        const r = await api.get("/bases/atividades/listar", { params: { limit: 100000 } });
        setAtividades(pickArray(r));
      } catch {
        try { setAtividades(pickArray(await getAtividadesNomes())); } catch { setAtividades([]); }
      }

      try {
        const r = await api.get("/bases/ativos/listar", { params: { limit: 100000 } });
        setAtivos(pickArray(r));
      } catch {
        try { setAtivos(pickArray(await getAtivosNomes())); } catch { setAtivos([]); }
      }

      // Pátios globais
      const tryUrls = ["/bases/sb-origem-encerramento/listar", "/sb-origem-encerramento/listar"];
      let all = [];
      for (const url of tryUrls) {
        try {
          const resp = await api.get(url, { params: { limit: 100000 } });
          all = all.concat(pickArray(resp).map(normalizeSbItem));
        } catch {}
      }
      all = uniqSorted([...all, normalizeSbItem(prog.sbOrigem), normalizeSbItem(prog.sbEncerramento)]);
      setSbsAll(all);
    })();
  }, [prog.sbOrigem, prog.sbEncerramento]);

  /* Gerência -> Coordenadores */
  const prevGer = useRef(form.gerencia);
  useEffect(() => {
    const changed = prevGer.current !== form.gerencia;
    prevGer.current = form.gerencia;

    if (!form.gerencia) { setCoordenadores([]); return; }
    getCoordenadores(form.gerencia).then((l) => setCoordenadores(l || [])).catch(() => setCoordenadores([]));

    if (changed) {
      setForm((f) => ({ ...f, coordenador: "", sub: "", kmValido:true, sbMsg:"" }));
      setSubs([]); setSbsCoord([]);
    }
  }, [form.gerencia]);

  /* Coordenador -> SUBs + SBs (info) */
  const prevCoord = useRef(form.coordenador);
  useEffect(() => {
    const changed = prevCoord.current !== form.coordenador;
    prevCoord.current = form.coordenador;

    if (!form.coordenador) { setSubs([]); setSbsCoord([]); return; }

    Promise.all([getSubs(form.coordenador), getSbs(form.coordenador)])
      .then(([subsList, sbsList]) => {
        setSubs(subsList || []);
        setSbsCoord(uniqSorted((sbsList || []).map(normalizeSbItem)));
      })
      .catch(() => { setSubs([]); setSbsCoord([]); });

    if (changed) setForm((f) => ({ ...f, sub:"", kmValido:true, sbMsg:"" }));
  }, [form.coordenador]);

  /* Prefill pátios */
  useEffect(() => {
    if (!sbsAll.length) return;
    setForm((f) => ({
      ...f,
      sbOrigem: f.sbOrigem || prog.sbOrigem || sbsAll[0] || "",
      sbEncerramento: f.sbEncerramento || prog.sbEncerramento || sbsAll[0] || "",
    }));
  }, [sbsAll, prog.sbOrigem, prog.sbEncerramento]);

  /* SUB + KM -> SB de trabalho informativo */
  const prevTriad = useRef("");
  useEffect(() => {
    const sub = form.sub;
    const ki = Number(cleanDec(form.kmInicio));
    const kf = Number(cleanDec(form.kmFinal));
    const key = [sub || "", Number.isNaN(ki) ? "" : ki, Number.isNaN(kf) ? "" : kf].join("|");
    if (prevTriad.current === key) return;
    prevTriad.current = key;

    if (sub && Number.isFinite(ki) && Number.isFinite(kf)) {
      Promise.all([validarKm(sub, ki, kf), sbKm(sub, ki, kf)])
        .then(([val, sbsList]) => {
          const arr = (Array.isArray(sbsList) ? sbsList : []).map(normalizeSbItem).filter(Boolean);
          setForm((f) => ({
            ...f,
            kmValido: !!val?.existe,
            sbDeTrabalho: val?.existe ? (arr.length ? arr.join(", ") : "") : "",
            sbMsg: val?.existe ? (arr.length ? "" : "Não há SB para o intervalo informado.") : "KM fora da SUB.",
          }));
        })
        .catch(() => setForm((f) => ({ ...f, kmValido:false, sbDeTrabalho:"", sbMsg:"Erro ao buscar KM." })));
    } else {
      setForm((f) => ({ ...f, kmValido:true, sbDeTrabalho:"", sbMsg:"" }));
    }
  }, [form.sub, form.kmInicio, form.kmFinal]);

  /* Handlers */
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleKmChange = (k, raw) => setField(k, cleanDec(raw));
  const blockWheel = (e) => e.currentTarget.blur();
  const blockArrows = (e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); };

  const handleAtividadeChange = (nextValue) => {
    const value =
      typeof nextValue === "string" ? nextValue : nextValue?.target?.value;
    const uni = atividadeIndex.get(keyify(value))?.unidade || "";
    setForm((f) => ({ ...f, atividade:value, unidade_medida:uni, ativo:"", qtd:"" }));
  };

  /* Critérios de split */
  const atividadeOriginalKey = keyify(prog.atividade);
  const sbTrabalhoOriginal  = String(prog.sbDeTrabalho || "").trim();
  const atividadeAlterada   = keyify(form.atividade) !== atividadeOriginalKey && !!form.atividade;
  const sbTrabalhoAlterado  = String(form.sbDeTrabalho || "").trim() !== sbTrabalhoOriginal;

  /* ===== Salvar ===== */
  const salvar = async () => {
    if (!atividadeValida) {
      return showErr("Selecione uma Atividade válida da lista.", atividadeRef);
    }
    if (atividadeAlterada) {
      if (!ativoValido) return showErr("Ao alterar a atividade, selecione um Ativo válido.", ativoRef);
      const qtdNum = Number(form.qtd);
      if (!(Number.isFinite(qtdNum) && qtdNum > 0)) {
        return showErr("Ao alterar a atividade, informe uma Quantidade maior que zero.", qtdRef);
      }
    }

    const kmInicioNum = form.kmInicio !== "" ? Number(cleanDec(form.kmInicio)) : null;
    const kmFinalNum  = form.kmFinal  !== "" ? Number(cleanDec(form.kmFinal )) : null;

    const atividadeCanonica = atividadeIndex.get(keyify(form.atividade));
    const ativoCanonico     = ativoIndex.get(keyify(form.ativo));

    const payloadBase = {
      ativo: ativoCanonico?.ativos || form.ativo || null,
      gerencia: form.gerencia || null,
      coordenador: form.coordenador || null,
      sub: form.sub || null,
      kmInicio: kmInicioNum,
      kmFinal: kmFinalNum,
      horario_do_intervalo: form.horario_do_intervalo || "",
      tempo_do_intervalo:   form.tempo_do_intervalo   || "",
      sbOrigem:       form.sbOrigem || "",
      sbEncerramento: form.sbEncerramento || "",
      qtd: form.qtd === "" ? null : Number(form.qtd),
      quantidade_maquinista: form.quantidade_maquinista === "" ? null : Number(form.quantidade_maquinista),
      maquinista:  form.maquinista || "",
      locomotiva1: form.locomotiva1 || "",
      locomotiva2: form.locomotiva2 || "",
      tipo_programacao: form.tipo_programacao || "",
      prioridade: (form.prioridade || "").toString().trim().toUpperCase(),
      tipo_acionamento: form.tipo_acionamento || "",
      etapa_nova: "execucao",
    };

    const shouldSplit = atividadeAlterada || sbTrabalhoAlterado;
    const payload = {
      ...payloadBase,
      ...(atividadeAlterada
        ? {
            atividade: atividadeCanonica?.atividade || form.atividade,
            unidade_medida: atividadeCanonica?.unidade || form.unidade_medida || "",
          }
        : {}),
      ...(sbTrabalhoAlterado ? { sbDeTrabalho: form.sbDeTrabalho || "" } : {}),
      ...(shouldSplit ? { split_on_critical_changes: true } : { update_only: true, force_no_split: true }),
    };

    setSalvando(true);
    try {
      const result = await api.post("/workflow/execucao/avancar", {
        ...payload,
        codigo_unico: prog.codigo_unico,
        observacao: prog.observacao || "",
        usuario_alteracao: JSON.parse(localStorage.getItem("usuario"))?.nome || "SISTEMA",
        motivo_alteracao: "Edição manual na execução",
      });
      const r = result?.data ?? result;

      // Observação: agora nova_codigo_unico refere-se à LINHA D1 limpa criada no split.
      onSaved({
        ...payload,
        _split: !!r?.split,
        _nova_codigo_unico: r?.nova_codigo_unico || null, // D1 limpa
        _original_permanece_execucao: !!r?.original_permanece_execucao,
      });
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Erro ao salvar.";
      showErr(msg);
    } finally {
      setSalvando(false);
    }
  };

  /* UI */
  return (
    <div className="editar-modal__backdrop" onClick={onClose}>
      <div className="editar-modal__container" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="editar-modal__header">
          <h3 className="editar-modal__title">Editar Programação</h3>
          <button className="editar-modal__close" aria-label="Fechar" onClick={onClose}>×</button>
        </div>

        <div className="editar-modal__body">
          <div className="editar-modal__grid">
            {/* linha 1 */}
            <div className="editar-field">
              <label className="editar-label">Atividade</label>
              <FieldDropdown
                triggerRef={atividadeRef}
                value={form.atividade || ""}
                onChange={handleAtividadeChange}
                options={atividadesOptions}
                placeholder="Selecione..."
                invalid={form.atividade && !atividadeValida}
                searchable
                searchPlaceholder="Buscar atividade..."
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">Ativo</label>
              <FieldDropdown
                triggerRef={ativoRef}
                value={form.ativo || ""}
                onChange={(nextValue) => setField("ativo", nextValue)}
                options={ativosOptions}
                placeholder="Selecione..."
                invalid={atividadeAlterada && form.ativo && !ativoValido}
                searchable
                searchPlaceholder="Buscar ativo..."
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">Unidade de Medida</label>
              <input className="inp readonly" readOnly value={form.unidade_medida || ""} />
            </div>

            {/* linha 2 */}
            <div className="editar-field">
              <label className="editar-label">Gerência</label>
              <FieldDropdown
                value={form.gerencia}
                onChange={(nextValue) => setField("gerencia", nextValue)}
                options={gerencias}
                placeholder="Selecione..."
                searchable
                searchPlaceholder="Buscar gerência..."
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">Coordenador</label>
              <FieldDropdown
                value={form.coordenador}
                onChange={(nextValue) => setField("coordenador", nextValue)}
                options={coordenadores}
                placeholder="Selecione..."
                disabled={!form.gerencia}
                searchable
                searchPlaceholder="Buscar coordenador..."
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">SUB</label>
              <FieldDropdown
                value={form.sub || ""}
                onChange={(nextValue) => setField("sub", nextValue)}
                options={subsIds}
                placeholder="Selecione..."
                disabled={!subsIds.length}
                searchable
                searchPlaceholder="Buscar SUB..."
              />
            </div>

            {/* linha 3 */}
            <div className="editar-field">
              <label className="editar-label">KM de Início</label>
              <input className="inp" type="text" inputMode="decimal" value={form.kmInicio} onChange={(e) => handleKmChange("kmInicio", e.target.value)} />
            </div>

            <div className="editar-field">
              <label className="editar-label">KM Final</label>
              <input className="inp" type="text" inputMode="decimal" value={form.kmFinal} onChange={(e) => handleKmChange("kmFinal", e.target.value)} />
            </div>

            <div className="editar-field">
              <label className="editar-label">Quantidade</label>
              <input
                ref={qtdRef}
                className="inp"
                type="number"
                min={0}
                step={1}
                value={form.qtd}
                onChange={(e) => setField("qtd", e.target.value)}
                onWheel={blockWheel}
                onKeyDown={blockArrows}
              />
            </div>

            {/* linha 4 */}
            <div className="editar-field">
              <label className="editar-label">Horário do Intervalo</label>
              <input className="inp" type="time" step="60" value={form.horario_do_intervalo || ""} onChange={(e) => setField("horario_do_intervalo", e.target.value)} />
            </div>

            <div className="editar-field">
              <label className="editar-label">Tempo do Intervalo</label>
              <input className="inp" type="time" step="60" value={form.tempo_do_intervalo || ""} onChange={(e) => setField("tempo_do_intervalo", e.target.value)} />
            </div>

            <div className="editar-field">
              <label className="editar-label">Prioridade</label>
              <FieldDropdown
                value={form.prioridade || ""}
                onChange={(nextValue) => setField("prioridade", nextValue)}
                options={["P0", "P1", "P2", "P3", "P4", "P5"]}
                placeholder="Selecionar..."
              />
            </div>

            {/* linha 5 */}
            <div className="editar-field">
              <label className="editar-label">Pátio de Origem</label>
              <FieldDropdown
                value={form.sbOrigem || ""}
                onChange={(nextValue) => setField("sbOrigem", nextValue)}
                options={sbsAll}
                placeholder="Selecione..."
                searchable
                searchPlaceholder="Buscar pátio..."
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">SB de Trabalho</label>
              <input className="inp readonly" readOnly value={form.sbDeTrabalho || ""} placeholder="Preenchido por SUB + KM" />
              {form.sbMsg && <small className="editar-label" style={{textTransform:"none"}}>{form.sbMsg}</small>}
            </div>

            <div className="editar-field">
              <label className="editar-label">Pátio de Encerramento</label>
              <FieldDropdown
                value={form.sbEncerramento || ""}
                onChange={(nextValue) => setField("sbEncerramento", nextValue)}
                options={sbsAll}
                placeholder="Selecione..."
                searchable
                searchPlaceholder="Buscar pátio..."
              />
            </div>

            {/* linha 6 */}
            <div className="editar-field">
              <label className="editar-label">Quantidade Maquinista</label>
              <input
                className="inp"
                type="number"
                min={0}
                step={1}
                value={form.quantidade_maquinista}
                onChange={(e) => setField("quantidade_maquinista", e.target.value)}
                onWheel={blockWheel}
                onKeyDown={blockArrows}
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">Maquinista</label>
              <FieldDropdown
                value={form.maquinista || ""}
                onChange={(nextValue) => setField("maquinista", nextValue)}
                options={["ESCALA", "FIXO", "FIXO + ESCALA"]}
                placeholder="Selecione"
              />
            </div>

            <div className="editar-field">
              <label className="editar-label">Tipo de Acionamento</label>
              <FieldDropdown
                value={form.tipo_acionamento || ""}
                onChange={(nextValue) => setField("tipo_acionamento", nextValue)}
                options={[
                  "ACIONAMENTO",
                  "AGUARDANDO ACIONAMENTO PCM",
                  "PCM",
                  "PREVENTIVA",
                  "CORRETIVA",
                  "INSPEÇÃO",
                  "EMERGENCIAL",
                ]}
                placeholder="Selecione"
              />
            </div>

            {/* linha 7 */}
            <div className="editar-field">
              <label className="editar-label">Locomotiva 1</label>
              <input className="inp" value={form.locomotiva1} onChange={(e) => setField("locomotiva1", e.target.value)} />
            </div>

            <div className="editar-field">
              <label className="editar-label">Locomotiva 2</label>
              <input className="inp" value={form.locomotiva2} onChange={(e) => setField("locomotiva2", e.target.value)} />
            </div>

            <div className="editar-field">
              <label className="editar-label">Tipo de Programação</label>
              <FieldDropdown
                value={form.tipo_programacao || ""}
                onChange={(nextValue) => setField("tipo_programacao", nextValue)}
                options={["CONTRATO", "OPORTUNIDADE"]}
                placeholder="Selecione"
              />
            </div>
          </div>
        </div>

        <div className="editar-modal__footer">
          <button className="editar-btn" onClick={onClose}>Cancelar</button>
          <button className="editar-btn" onClick={salvar} disabled={salvando || !atividadeValida}>
            {salvando ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>

        <ErrorDialog open={err.open} message={err.msg} onClose={() => setErr({ open:false, msg:"" })} />
      </div>
    </div>
  );
}
